import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router";
import { ago, elapsed, plural } from "../../../shared/captureFormat";
import {
  tallyStatuses,
  type Snapshot, type WireOfferResult, type WireProgress, type WireRunSummary,
} from "../../../shared/capturePort";
import { ExtensionUpdate } from "../components/ExtensionUpdate";
import { Fold } from "../components/Fold";
import { PageTitle, SectionTitle } from "../components/Heading";
import { Icon } from "../components/Icons";
import {
  batchRuns, finishedRun, isRunning, useCapture, type CaptureValue,
} from "../lib/captureContext";
import { fetchNewestRun, rejectRun, undoable, type CaptureRun } from "../lib/captureRuns";
import { fetchChanges, type PointsChange } from "../lib/pointsChanges";
import { fetchOfferChanges, type OfferChange } from "../lib/offerChanges";
import { allConfirmed, fetchRunAdds, type AddedOffer } from "../lib/offerAdds";
import { DismissAdd } from "../components/DismissAdd";
import { useAddDismissals } from "../components/addDismissals";
import { fetchPoints } from "../lib/pointsPage";
import { ChangesCard } from "../components/WhatChanged";
import { offerRow, pointsRow } from "../components/changeRows";
import {
  recordedReadings, sharedSlugs, type RecordedReading,
} from "../lib/refreshReadings";
import {
  RUN_WRITES, allOffersCommand, allQueued, combinedCommand, finishedWhen, isOverdue, isSourceOverdue,
  offerTrouble, offersCommand, pointsCommand, refreshSections, runAllCommand, runAllCounts, runOutcome,
  runProblems, slugNames, type IssuerSection,
} from "../lib/refreshPage";

/**
 * Refresh: Wallet drives the capture run, and the extension does it.
 *
 * Approved mockup, 2026-10-03. The shape of the page comes from one decision --
 * Run all is the headline and everything else is a row -- because one press is
 * the ordinary week and the per-row buttons are for the re-read after a rule fix.
 *
 * Nothing here knows the registry. Which issuer pairs with which program, which
 * sites share a host, what order a run takes: all of it arrives in the catalog
 * and is rendered as given. The page is reachable only when the extension has
 * answered a handshake, so there are no dead controls to hide.
 *
 * Privacy: every field the WIRE carries is a name, a count, a date, a status word
 * or a code -- no balance, no amount, no merchant, no card ending -- which is the
 * same rule the extension's own log keeps. The recorded balances under Last run
 * are not from the wire: they are read from Wallet's own API, like the Points
 * page, which is why the extension still stores no figure.
 */
export default function Refresh() {
  const capture = useCapture();

  // Still handshaking. Nothing is drawn rather than a redirect, which would race
  // the handshake and bounce off a page that was about to work.
  if (capture.connection === "connecting") {
    return (
      <div className="space-y-4">
        <PageTitle>Refresh</PageTitle>
        <p className="text-sm text-neutral-500">Looking for the capture extension…</p>
      </div>
    );
  }

  // No extension in this browser, which is every phone and every other profile.
  // There is one user and nothing to explain to them, so the route is not a page
  // at all: it lands where the readings land.
  if (capture.connection === "absent") return <Navigate to="/points" replace />;

  if (capture.connection === "outdated") return <Outdated capture={capture} />;

  return <Control capture={capture} />;
}

/**
 * The one unavailable state worth saying out loud.
 *
 * Actionable and otherwise invisible: the extension IS loaded, and a silent
 * redirect would read as "it is gone". One line and no controls, because none of
 * them would work.
 */
function Outdated({ capture }: { capture: CaptureValue }) {
  return (
    <div className="space-y-4">
      <PageTitle>Refresh</PageTitle>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-900 bg-amber-950/30 p-3 text-sm">
        <span className="font-semibold text-amber-400">Points Capture is too old</span>
        <span className="text-neutral-300">
          {capture.speaks === null
            ? "It does not speak this page's protocol."
            : `It speaks protocol ${capture.speaks}; this page needs ${1}.`}{" "}
          Rebuild it and reload it in Chrome.
        </span>
        <button type="button" onClick={capture.retry} className="btn btn-ghost btn-sm ml-auto">
          Check again
        </button>
      </div>
    </div>
  );
}

/** The page proper: the run, then everything that can start one. */
function Control({ capture }: { capture: CaptureValue }) {
  const { catalog, snapshot } = capture;
  const live = isRunning(capture);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  // Memoised because `Control` re-renders on every pushed snapshot -- around 60 to
  // 100 times in a twelve-program run -- while the catalog changes almost never.
  const sections = useMemo(
    () => (catalog ? refreshSections(catalog) : { issuers: [], programs: [] }), [catalog]);
  const counts = useMemo(
    () => (catalog ? runAllCounts(catalog) : { programs: 0, sources: 0 }), [catalog]);
  // The handshake can be good while the catalog is not, because the catalog comes
  // from Wallet's API and the connection does not. Three states, and drawing them
  // the same was a bug: a page with Run all over no rows reads as "there is
  // nothing to capture". (2026-10-04)
  const catalogLoading = !catalog && !capture.catalogFailed;
  const catalogBroken = capture.catalogFailed;

  async function run(slugs: string[] | undefined) {
    // While a run goes, a row's command waits its turn instead of being refused.
    if (live && slugs) {
      capture.enqueue(slugs);
      return;
    }
    setBusy(true);
    setRefused(null);
    const answer = await capture.start(slugs);
    setBusy(false);
    if ("error" in answer) {
      setRefused(answer.error === "busy"
        ? "A run is already going."
        : "The extension did not take that. Try again.");
    }
  }

  // Disabled while a run goes, matching the extension's one-run-at-a-time rule
  // rather than queueing: a queue is real work on both sides and nobody has
  // wanted it yet.
  // Nothing to start while the page cannot say what a run would cover. Run all
  // would in fact work -- it sends no filter -- but a button whose own label says
  // "0 programs" is not one to offer.
  const canStart = !live && !busy && catalog !== null;
  // A row can also be pressed while an individual run goes: it queues. Not
  // behind Run all, which already reads everything, and Run all itself never
  // queues.
  const canPress = !busy && catalog !== null && !capture.runAllLive;
  const queued = capture.queued;
  const names = useMemo(() => (catalog ? slugNames(catalog) : new Map<string, string>()), [catalog]);
  const finished = finishedRun(capture);

  return (
    <div className="space-y-5">
      <PageTitle>Refresh</PageTitle>

      <ExtensionUpdate capture={capture} />

      {/* The scope. A control in global chrome implies it refreshes the app, and
          it does not: these three are what a run writes. */}
      <p className="max-w-[64ch] text-[13px] leading-relaxed text-neutral-500">
        Reads your accounts in this browser and updates{" "}
        {RUN_WRITES.map((tab, i) => (
          <span key={tab}>
            {i > 0 && (i === RUN_WRITES.length - 1 ? " and " : ", ")}
            <span className="font-semibold text-neutral-200">{tab}</span>
          </span>
        ))}
        . Travel fares arrive on their own; Credits and Cards are not read this way.
      </p>

      <Versions capture={capture} />

      {catalogLoading && (
        <p className="text-sm text-neutral-500">Loading what can be read…</p>
      )}

      {catalogBroken && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-900 bg-amber-950/25 p-3 text-sm">
          <span className="font-semibold text-amber-400">
            {catalog ? "Could not refresh the list" : "Could not load the list"}
          </span>
          <span className="text-neutral-300">
            The extension is connected; Wallet did not answer for its rules.
            {catalog && " Showing the list from a moment ago."}
          </span>
          <button
            type="button"
            onClick={capture.retry}
            className="btn btn-ghost btn-sm ml-auto"
          >
            Try again
          </button>
        </div>
      )}

      {live && snapshot ? (
        <Live snapshot={snapshot} onStop={capture.stop} />
      ) : (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-800 bg-neutral-900 p-3.5">
          <div className="min-w-[14rem] flex-1">
            <h3 className="text-[15px] font-bold">Run all</h3>
            <p className="text-xs text-neutral-500">
              {counts.programs} programs and {counts.sources} offers pages, each read once.
            </p>
          </div>
          {counts.sources > 0 && (
            <button
              type="button"
              onClick={() => { if (catalog) void run(allOffersCommand(catalog)); }}
              disabled={!canStart}
              className="btn btn-ghost"
            >
              All card offers
            </button>
          )}
          <button
            type="button"
            onClick={() => void run(runAllCommand())}
            disabled={!canStart}
            className="btn btn-primary"
          >
            <Icon name="capture" size={16} />
            Run all
          </button>
        </div>
      )}

      {refused && <p className="text-[13px] text-amber-400">{refused}</p>}

      {queued.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-neutral-800 bg-neutral-900 p-3 text-[13px]">
          <span className="font-semibold">{live ? "Queued next" : "Queued"}</span>
          <span className="min-w-0 text-neutral-300">
            {queued.map((slug) => names.get(slug) ?? slug).join(", ")}
          </span>
          {capture.queueFailed && (
            <span className="text-amber-400">The extension did not take the queue. Run it again.</span>
          )}
          <span className="ml-auto flex gap-1.5">
            {!live && (
              <button type="button" onClick={() => void run(queued)} className="btn btn-sm">
                Run queued
              </button>
            )}
            <button type="button" onClick={capture.clearQueue} className="btn btn-ghost btn-sm">
              Clear
            </button>
          </span>
        </div>
      )}

      {/* The last run leads the page, right under Run all: what it changed is
          what arriving here after a run asks. Starting the next run clears it. */}
      {finished && <LastRun run={finished} runIds={batchRuns(capture)} />}

      {live && (
        <p className="text-xs text-neutral-500">
          You can leave this page. The run carries on in the background.
          {!capture.runAllLive && " Pressing a row now queues it for when this run finishes."}
        </p>
      )}

      {sections.issuers.length > 0 && (
        <section className="space-y-2">
          <div className="flex flex-wrap items-baseline gap-2.5">
            <SectionTitle>Card issuers</SectionTitle>
            <span className="text-[11px] text-neutral-500">
              points and offers, paired where the catalog says so
            </span>
          </div>
          <div className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900">
            {sections.issuers.map((issuer) => (
              <IssuerRow
                key={issuer.offers?.slug ?? issuer.issuer}
                issuer={issuer}
                canStart={canPress}
                queued={queued}
                onRun={(slugs) => void run(slugs)}
              />
            ))}
          </div>
        </section>
      )}

      {sections.programs.length > 0 && (
        <section className="space-y-2">
          <div className="flex flex-wrap items-baseline gap-2.5">
            <SectionTitle>Other programs</SectionTitle>
            <span className="text-[11px] text-neutral-500">
              {sections.programs.length} with a verified rule
            </span>
          </div>
          <div className="grid overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900 sm:grid-cols-2">
            {sections.programs.map((program) => (
              <div
                key={program.slug}
                className="flex items-center gap-3 border-t border-neutral-800 p-3 first:border-t-0 sm:[&:nth-child(2)]:border-t-0 sm:[&:nth-child(even)]:border-l"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px]">{program.name}</div>
                  <Freshness day={program.last_as_of} overdue={isOverdue(program, new Date())} />
                </div>
                <RowButton
                  slugs={pointsCommand(program.slug)}
                  label="Run"
                  canStart={canPress}
                  queued={queued}
                  live={live}
                  onRun={(slugs) => void run(slugs)}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* The extension keeps the log until the next run starts, and it is the
          only place a failure's cause is written down: the summary carries
          codes, the log says what the page was doing when it happened. */}
      {!live && snapshot?.last_run && snapshot.log.length > 0 && (
        <Log lines={snapshot.log} title="Run log" startOpen={false} />
      )}
      {!live && snapshot?.interrupted && (
        <p className="text-[13px] text-amber-400">
          The last run stopped unexpectedly. Nothing from it was sent.
        </p>
      )}
    </div>
  );
}

/** Which build is on each side, and whether the rules in hand are the deployed ones. */
function Versions({ capture }: { capture: CaptureValue }) {
  const { hello, catalog, connection } = capture;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-neutral-800 bg-neutral-900 px-3 py-2.5 text-xs">
      <span className="rounded-full border border-green-900 bg-green-950/30 px-2 py-0.5 font-semibold text-green-400">
        Extension {hello?.extension ?? "unknown"}
      </span>
      {connection === "reconnecting" ? (
        <span className="text-amber-400">reconnecting…</span>
      ) : (
        <span className="text-neutral-500">connected</span>
      )}
      {catalog?.rules_version && (
        <>
          <span className="text-neutral-700">·</span>
          <span className="font-mono text-neutral-500">rules {catalog.rules_version.slice(0, 8)}</span>
        </>
      )}
    </div>
  );
}

/**
 * "read 3 days ago", with an overdue badge when it is past its cadence.
 *
 * One component for a program and for an offers source: the two are the same
 * fact about different things, and two renderings of it would drift.
 */
function Freshness({
  day,
  overdue,
  className = "mt-0.5",
}: {
  day: string | null | undefined;
  overdue: boolean;
  className?: string;
}) {
  return (
    <span className={`flex flex-wrap items-center gap-1.5 text-[11.5px] text-neutral-500 ${className}`}>
      <span>read {ago(day ?? null, new Date())}</span>
      {overdue && (
        <span className="rounded-full border border-amber-900/70 px-1.5 text-[10.5px] font-semibold text-amber-400">
          overdue
        </span>
      )}
    </span>
  );
}

/**
 * One issuer: its points, its offers, and the paired run.
 *
 * "Points + offers" is drawn only when the REGISTRY paired them, which is what
 * keeps Chase honest: its points and offers are on two different sites, so one
 * press is still two sign-ins. The row says so because the catalog pairs them,
 * not because this file knows anything about chase.com.
 */
function IssuerRow({
  issuer,
  canStart,
  queued,
  onRun,
}: {
  issuer: IssuerSection;
  canStart: boolean;
  queued: string[];
  onRun: (slugs: string[]) => void;
}) {
  const live = isRunning(useCapture());
  return (
    <div className="flex flex-wrap items-start gap-3 border-t border-neutral-800 p-3 first:border-t-0">
      <div className="min-w-[12rem] flex-1">
        <div className="text-sm font-semibold">{issuer.issuer}</div>
        <div className="mt-1 flex flex-col gap-0.5">
          {issuer.points.map((program) => (
            <div key={program.slug} className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11.5px] text-neutral-300">{program.name}</span>
              <Freshness
                day={program.last_as_of}
                overdue={isOverdue(program, new Date())}
                className=""
              />
            </div>
          ))}
          {issuer.points.length === 0 && (
            <div className="text-[11.5px] text-neutral-500">
              No points rule paired with this issuer yet.
            </div>
          )}
          {issuer.offers && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11.5px] text-neutral-300">Offers</span>
              <Freshness
                day={issuer.offers.last_read_on}
                overdue={isSourceOverdue(issuer.offers, new Date())}
                className=""
              />
            </div>
          )}
          {/* An issuer Wallet knows only through a program's `issuer`: its
              points are captured and its offers page is not served, so there is
              nothing to press for offers and the row says why. */}
          {!issuer.offers && (
            <div className="text-[11.5px] text-neutral-500">
              Offers page not verified yet.
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {issuer.points.length > 0 && (
          <RowButton
            slugs={issuer.points.flatMap((p) => pointsCommand(p.slug))}
            label="Points"
            className="btn btn-ghost btn-sm"
            canStart={canStart}
            queued={queued}
            live={live}
            onRun={onRun}
          />
        )}
        {issuer.offers && (
          <RowButton
            slugs={offersCommand(issuer.offers!.slug)}
            label="Offers"
            className="btn btn-ghost btn-sm"
            canStart={canStart}
            queued={queued}
            live={live}
            onRun={onRun}
          />
        )}
        {issuer.combined && issuer.offers && (
          <RowButton
            slugs={combinedCommand(issuer.offers!)}
            label="Points + offers"
            className="btn btn-sm"
            canStart={canStart}
            queued={queued}
            live={live}
            onRun={onRun}
          />
        )}
      </div>
    </div>
  );
}

/**
 * A row's run button. While a run goes it queues instead, and says so once
 * everything it would read is already queued.
 */
function RowButton({
  slugs, label, className = "btn btn-ghost btn-sm", canStart, queued, live, onRun,
}: {
  slugs: string[];
  label: string;
  className?: string;
  canStart: boolean;
  queued: string[];
  live: boolean;
  onRun: (slugs: string[]) => void;
}) {
  const waiting = allQueued(queued, slugs);
  return (
    <button
      type="button"
      disabled={!canStart || waiting}
      onClick={() => onRun(slugs)}
      className={className}
      title={live && !waiting ? "Queue for when this run finishes" : undefined}
    >
      {waiting ? `${label} queued` : live ? `Queue ${label.toLowerCase()}` : label}
    </button>
  );
}

/**
 * A run in flight: what is done, what is in hand, and what it is waiting for.
 *
 * Waiting on the PERSON is the one state that must not look like progress, so it
 * gets its own banner above the panel. The clock ticks here rather than in the
 * bridge: the extension sends `since` once and the seconds are this page's to
 * count.
 */
function Live({ snapshot, onStop }: { snapshot: Snapshot; onStop: () => Promise<void> }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => { setNow(Date.now()); }, 1000);
    return () => { clearInterval(timer); };
  }, []);

  const progress = snapshot.progress;
  const waiting = progress?.current.find((c) => c.waiting);

  return (
    <div className="space-y-3">
      {waiting && <Waiting waiting={waiting} now={now} />}
      <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-3.5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">
              {progress ? `${progress.done.length} of ${progress.total} done` : "Starting…"}
            </div>
            <div className="text-xs text-neutral-500">
              {snapshot.stopping
                ? "Stopping \u2014 closed the tabs it had open"
                : "Reading one site at a time"}
            </div>
          </div>
          <button type="button" onClick={() => void onStop()} className="btn btn-danger btn-sm">
            Stop
          </button>
        </div>
        {progress && progress.total > 0 && (
          <div className="meter mt-2.5">
            <i style={{ width: `${Math.round((progress.done.length / progress.total) * 100)}%` }} />
          </div>
        )}
        {progress && <Steps progress={progress} now={now} />}
        {snapshot.log.length > 0 && <Log lines={snapshot.log} />}
      </div>
    </div>
  );
}

function Waiting({
  waiting,
  now,
}: {
  waiting: NonNullable<WireProgress["current"]>[number];
  now: number;
}) {
  return (
    <div className="rounded-xl border border-amber-900 bg-amber-950/25 p-3.5">
      <h3 className="text-sm font-bold text-amber-400">
        {`${waiting.name} is asking you to sign in`}
      </h3>
      <p className="mt-1 text-[13px] leading-relaxed text-neutral-300">
        Finish signing in on the tab the run opened and it carries on by itself.{" "}
        <span className="text-neutral-500">waiting {elapsed(waiting.since, now)}</span>
      </p>
    </div>
  );
}

/** Programs read, and the ones in hand. Counts only: Wallet says what was recorded. */
function Steps({ progress, now }: { progress: WireProgress; now: number }) {
  return (
    <ul className="mt-3 flex flex-col gap-1.5 text-[12.5px]">
      {progress.done.map((p) => {
        const what = p.counts === "cards" ? "cards read" : "read";
        const [text, tone] =
          p.note === "stopped"
            ? ["stopped", "text-neutral-400"]
            : p.note === "error"
              ? ["could not run", "text-red-400"]
              : p.note === "signed_out"
                ? ["needs sign-in", "text-amber-400"]
                : [`${p.read} of ${p.of} ${what}`,
                  p.read === p.of ? "text-green-400" : "text-amber-400"];
        return (
          <li key={p.slug} className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate">{p.name}</span>
            <span className={tone}>{text}</span>
          </li>
        );
      })}
      {progress.current.map((c) => (
        <li key={c.slug} className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate font-semibold text-neutral-50">{c.name}</span>
          <span className={c.waiting ? "text-amber-400" : "text-accent"}>
            {c.waiting ? "waiting for you to sign in" : "reading"}{" "}
            · {elapsed(c.since, now)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The run's own log, newest last.
 *
 * Offsets rather than clock times, because what you want from it is "this
 * program has been on one reading for ninety seconds". Codes, rule keys, slugs
 * and hosts only.
 */
function Log({ lines, title = "Live log", startOpen = true }: {
  lines: Snapshot["log"]; title?: string; startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  return (
    <>
      <Fold
        open={open}
        onClick={() => { setOpen(!open); }}
        label={<>{title} <span className="text-neutral-500">{lines.length} lines</span></>}
      />
      {open && (
        <ol className="mt-2 max-h-48 overflow-auto rounded-lg border border-neutral-800 bg-neutral-950 p-2.5 font-mono text-[11.5px] leading-relaxed">
          {lines.map((line, i) => (
            <li key={`${line.at}-${i}`} className="flex gap-2.5">
              <span className="shrink-0 text-neutral-600">
                {line.at < 0 ? "-" : "+"}
                {(Math.abs(line.at) / 1000).toFixed(1)}s
              </span>
              <span className="break-words text-neutral-300">{line.text}</span>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

const PROGRAM_STATE: Record<WireRunSummary["programs"][number]["state"], [string, string]> = {
  recorded: ["Read", "text-green-400"],
  partial: ["Partly read", "text-amber-400"],
  failed: ["Page changed", "text-red-400"],
  signed_out: ["Needs sign-in", "text-amber-400"],
  error: ["Could not run", "text-red-400"],
  // Neutral, and deliberately not red: you did this. It used to render as "Could
  // not run", which blamed the site for the button you pressed. (2026-10-04)
  stopped: ["Stopped", "text-neutral-400"],
};

const OUTCOME_TONE = {
  bad: "text-red-400", warn: "text-amber-400", good: "text-green-400",
} as const;

/**
 * What the last run did, which is what arriving at this page usually asks.
 *
 * Folded shut by default and led by what needs a look, because the ordinary
 * result of re-reading a balance that has not moved is a list of green rows
 * nobody needs to read.
 */
function LastRun({ run, runIds }: { run: WireRunSummary; runIds: string[] }) {
  const [open, setOpen] = useState(false);
  const { programs: problems, offers: offerProblems } = runProblems(run);
  const offers = run.offers ?? [];
  const outcome = runOutcome(run);

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <SectionTitle>Last run</SectionTitle>
        <span className={`text-[11.5px] font-semibold ${OUTCOME_TONE[outcome.tone]}`}>
          {outcome.text}
        </span>
        {run.stopped && <span className="text-[11.5px] text-neutral-500">stopped early</span>}
        <span className="text-[11.5px] text-neutral-500">
          finished {finishedWhen(run.finished_at, new Date())}
          {runIds.length > 1 && `, with ${plural(runIds.length - 1, "queued run")} before it`}
        </span>
      </div>
      <RunChanges runIds={runIds} />
      <RunAdds runIds={runIds} />
      {/* Named, not counted: the headline's count is useless without them. */}
      {problems.length + offerProblems.length > 0 && (
        <ul className="flex flex-col gap-1 text-[12.5px]">
          {problems.map((p) => {
            const [text, tone] = PROGRAM_STATE[p.state];
            const why = p.failures.map((f) => `${f.rule_key}: ${f.code}`).join("; ");
            return (
              <li key={p.slug} className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold">{p.name}</span>
                <span className={tone}>{text}</span>
                {why && <span className="text-[11.5px] text-neutral-500">{why}</span>}
              </li>
            );
          })}
          {offerProblems.map((source) => {
            const trouble = offerTrouble(source);
            if (!trouble) return null;
            return (
              <li key={source.slug} className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold">{source.issuer} offers</span>
                {sourceState(source)}
                {trouble.detail
                  && <span className="text-[11.5px] text-neutral-500">{trouble.detail}</span>}
              </li>
            );
          })}
        </ul>
      )}
      <Recorded run={run} />
      <Fold
        open={open}
        onClick={() => { setOpen(!open); }}
        label={
          <>
            {plural(run.programs.length, "program")}
            {offers.length > 0 && `, ${plural(offers.length, "offers page")}`}
          </>
        }
      />
      {open && (
        <ul className="flex flex-col gap-2 rounded-xl border border-neutral-800 bg-neutral-900 p-3 text-[12.5px]">
          {run.programs.map((p) => {
            const [text, tone] = PROGRAM_STATE[p.state];
            // Balances and credits apart: one slug carries one balance and, on
            // Amex, nine credits, and "10 recorded" reads as ten figures.
            const took = tallyStatuses(p.statuses);
            const notes = [
              ...(took.balances ? [plural(took.balances, "balance")] : []),
              ...(took.credits ? [plural(took.credits, "credit")] : []),
              ...(took.unkinded ? [`${took.unkinded} recorded`] : []),
              ...(took.held ? [`${took.held} held for review`] : []),
              ...p.failures.map((f) => `${f.rule_key}: ${f.code}`),
            ];
            return (
              <li key={p.slug}>
                <span className="font-semibold">{p.name}</span>{" "}
                <span className={tone}>{text}</span>
                {notes.length > 0 && (
                  <div className="text-[11.5px] text-neutral-500">{notes.join("; ")}</div>
                )}
              </li>
            );
          })}
          {offers.map((source) => (
            <li key={source.slug}>
              <span className="font-semibold">{source.issuer} offers</span>{" "}
              {/* Through `offerTrouble`, so this cannot call a source "read" while
                  the summary above says its cards failed. It used to: the chain
                  here never looked at the cards, so one render made two claims
                  about one source. */}
              {sourceState(source)}
              <ul className="mt-1 flex flex-col gap-1 border-l border-neutral-800 pl-3 text-[11.5px] text-neutral-500">
                {source.cards.map((card) => (
                  <li key={card.card}>
                    {card.card} — {card.read} read; {card.skipped} skipped
                    {(card.reason || card.failures.length > 0) && (
                      <span className="text-red-400">
                        {" · "}
                        {[...(card.reason ? [card.reason] : []), ...card.failures].join(", ")}
                      </span>
                    )}
                    {card.details && (
                      <span>
                        {" · "}details {card.details.read} of {card.details.wanted} read
                        {card.details.stopped && (
                          <span className="text-amber-400">
                            {", stopped: "}{card.details.stopped}
                          </span>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-1.5">
        <Link to="/points" className="btn btn-ghost btn-sm">See Points</Link>
        <Link to="/offers" className="btn btn-ghost btn-sm">See Offers</Link>
        <Undo runId={run.run_id} />
      </div>
    </section>
  );
}

/**
 * What to call an offers source's outcome, in one place.
 *
 * Both the summary above and the full list below read it, so the page cannot call
 * one source "read" in one list and "2 of 2 cards failed" in the other.
 */
function sourceState(source: WireOfferResult) {
  if (source.stopped) return <span className="text-neutral-400">stopped</span>;
  const trouble = offerTrouble(source);
  if (!trouble) return <span className="text-green-400">read</span>;
  return (
    <span className={source.signed_out ? "text-amber-400" : "text-red-400"}>
      {trouble.text}
    </span>
  );
}

/**
 * The balances this run recorded, read from Wallet's own API; `refreshReadings.ts`
 * holds why they come from there and not from the extension. Folded: what MOVED
 * is in the changes card above, and these are the check you make when a rule is
 * suspect.
 *
 * A run that recorded nothing draws nothing: the program rows above already say
 * why, and an empty panel would read as a failure of its own.
 */
function Recorded({ run }: { run: WireRunSummary }) {
  const [readings, setReadings] = useState<RecordedReading[] | null>(null);
  const [allShown, setAllShown] = useState(false);
  // Joined, so the effect's dependency is stable BY VALUE. The extension pushes a
  // fresh snapshot per storage write and each push is a structured clone, so an
  // array or a memo over `run.programs` is a new identity every time and re-fires
  // the effect: measured at three calls to the app's slowest route for one run.
  // A slug may not contain a comma (the registry refuses one), so this round-trips.
  const slugKey = run.programs.map((p) => p.slug).join(",");

  useEffect(() => {
    // Never the run before this one's figures, whose balance would sit under this
    // run's heading.
    setReadings(null);
    const slugs = slugKey ? slugKey.split(",") : [];
    // An offers-only run recorded no balance, and `/api/points` is the app's
    // slowest call, so it is not made at all.
    if (slugs.length === 0) return;
    let stale = false;
    // A failure costs the figures, never the page: everything above this is the
    // extension's own report and does not depend on Wallet answering.
    fetchPoints()
      .then((dto) => {
        if (!stale) setReadings(recordedReadings(dto, slugs));
      })
      .catch(() => undefined);
    return () => { stale = true; };
  }, [run.run_id, slugKey]);

  if (!readings || readings.length === 0) return null;
  const labelled = sharedSlugs(readings);

  return (
    <div className="space-y-1">
      <Fold
        open={allShown}
        onClick={() => { setAllShown(!allShown); }}
        label={<>{plural(readings.length, "balance")} recorded</>}
      />
      {allShown && (
        <ul className="flex flex-col gap-1 text-[12.5px]">
          {readings.map((r) => (
            <li key={`${r.slug}:${r.label}`} className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-neutral-400">{r.program}</span>
              {labelled.has(r.slug) && <span className="text-[11.5px] text-neutral-500">{r.label}</span>}
              <span className="font-semibold tabular-nums">{r.balance}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * What the last run (with any queued before it) changed, from Wallet's own
 * change feeds: the Points half shows each run's own move, the Offers half the
 * new offers worth adding it found. Dismissing here dismisses on Points and
 * Offers too, and anything left undismissed stays there after the next run
 * clears this card. A reading the run had held is approved or rejected here.
 */
function RunChanges({ runIds }: { runIds: string[] }) {
  const [points, setPoints] = useState<PointsChange[]>([]);
  const [offers, setOffers] = useState<OfferChange[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState(0);
  const capture = useCapture();
  const key = runIds.join(",");

  useEffect(() => {
    let stale = false;
    const mine = new Set(key.split(","));
    // Each half on its own: one feed failing costs its half, never the other.
    fetchChanges()
      .then((all) => { if (!stale) setPoints(all.filter((c) => c.run && mine.has(c.run.run_id))); })
      .catch(() => undefined);
    fetchOfferChanges()
      .then((all) => { if (!stale) setOffers(all.filter((c) => mine.has(c.run_id))); })
      .catch(() => undefined);
    return () => { stale = true; };
    // `finished` too: a second run of the same batch adds to it; `reviewed`,
    // because an approved hold comes back as an ordinary move.
  }, [key, capture.finished, reviewed]);

  return (
    <>
      <ChangesCard
        title="Changed this run"
        onError={setError}
        onReviewed={() => setReviewed((n) => n + 1)}
        groups={[
          { label: "Points", link: { to: "/points", text: "Open Points →" },
            rows: points.map((c) => pointsRow(c, "run")) },
          { label: "Offers", link: { to: "/offers", text: "Open Offers →" },
            rows: offers.map((c) => offerRow(c)) },
        ]}
      />
      {error && <p className="text-[12.5px] text-red-400">{error}</p>}
    </>
  );
}

/** Why one card's add is not confirmed, and what happens next. */
function unconfirmedNote(card: AddedOffer["cards"][number]): string {
  if (card.state === "vanished") return "left the card's list after the press; check the issuer's site";
  if (card.gave_up) return "did not take twice; add it on the issuer's site";
  return "tried again next run";
}

/**
 * What the last run (with any queued before it) added to your cards, one card
 * per issuer, from Wallet's own record of the presses. An offer is "Added"
 * once every card's next read confirmed it; the rest are named in an amber
 * footer, since the next run presses them again. Each can be dismissed, which
 * also clears it from the Offers page. Nothing added draws nothing.
 */
function RunAdds({ runIds }: { runIds: string[] }) {
  const [added, setAdded] = useState<AddedOffer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const dismissals = useAddDismissals(setError);
  const capture = useCapture();
  const key = runIds.join(",");

  useEffect(() => {
    let stale = false;
    setAdded([]);
    setError(null);
    if (!key) return;
    fetchRunAdds(key.split(","))
      .then((all) => { if (!stale) setAdded(all); })
      .catch((e: unknown) => {
        if (!stale) setError(e instanceof Error ? e.message : "Could not load what the run added");
      });
    return () => { stale = true; };
    // `finished` too: a second run of the same batch adds to it.
  }, [key, capture.finished]);

  if (error) return <p className="text-[12.5px] text-red-400">{error}</p>;
  // A card since retired from the registry has no issuer, and still gets a card.
  const shown = added.filter((a) => !dismissals.isHidden(a.key));
  const issuers = [...new Set(shown.map((a) => a.issuer ?? ""))];
  return (
    <>
      {dismissals.toast}
      {issuers.map((issuer) => {
        const offers = shown.filter((a) => (a.issuer ?? "") === issuer);
        const title = issuer ? `Added to your ${issuer} cards` : "Added to your cards";
        const unconfirmed = offers.flatMap((a) => a.cards
          .filter((c) => c.state !== "confirmed")
          .map((c) => ({ merchant: a.merchant, card: c.card_name, note: unconfirmedNote(c) })));
        return (
          <section
            key={issuer}
            aria-label={title}
            className="rounded-xl border border-neutral-800 bg-neutral-900"
          >
            <div className="flex items-center gap-2 border-b border-neutral-800 py-1.5 pl-3 pr-1.5">
              <h3 className="text-sm font-semibold">{title}</h3>
              <button
                type="button"
                onClick={() => { dismissals.hide(offers.map((a) => a.key)); }}
                className="btn btn-ghost btn-sm ml-auto"
              >
                Dismiss all
              </button>
            </div>
            <ul className="divide-y divide-neutral-800">
              {offers.map((a) => (
                <li key={a.key} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-3 py-2.5">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-[13.5px] font-semibold">{a.merchant}</span>
                    {a.terms && <span className="text-[12.5px] text-neutral-300">{a.terms}</span>}
                    <span className="mt-0.5 flex flex-wrap gap-1.5 text-xs text-neutral-400">
                      {a.cards.map((c) => (
                        <span key={c.card_name} className="rounded-md bg-neutral-800 px-2 py-px">{c.card_name}</span>
                      ))}
                    </span>
                  </div>
                  {allConfirmed(a) && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-green-400">
                      <Icon name="check" size={14} strokeWidth={2.2} />
                      Added
                    </span>
                  )}
                  <DismissAdd merchant={a.merchant} onClick={() => { dismissals.hide([a.key]); }} />
                </li>
              ))}
            </ul>
            {unconfirmed.length > 0 && (
              <div className="border-t border-neutral-800 px-3 py-2.5 text-[12.5px]">
                <p className="font-semibold text-amber-400">Not confirmed · {unconfirmed.length}</p>
                <ul className="mt-1 flex flex-col gap-0.5 text-neutral-400">
                  {unconfirmed.map((u) => (
                    <li key={`${u.merchant}:${u.card}`}>
                      {u.merchant} · {u.card}{" "}
                      <span className="text-neutral-500">
                        {u.note}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}

/**
 * Undo the whole run, if there is anything left to put back.
 *
 * Moved here from the extension's popup in 0.3.0. It goes through Wallet's own
 * API, not the capture port: `rejectRun` is deliberately not one of the six verbs
 * a web page may send to the extension, and it does not need to be, because this
 * is Wallet's own authenticated route.
 *
 * The counts come from Wallet rather than from the extension's snapshot, because
 * the extension reports what it SENT and only Wallet knows what it kept -- a
 * reading held for review is nothing to undo. Deliberately quiet: a small link
 * with an inline confirm, never a headline button, and absent entirely when the
 * run has already been undone.
 */
function Undo({ runId }: { runId: string }) {
  const [newest, setNewest] = useState<CaptureRun | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    // A failure here costs the button, never the page: the result above it is
    // the extension's and does not depend on this.
    fetchNewestRun()
      .then((run) => { if (!stale) setNewest(run); })
      .catch(() => undefined);
    return () => { stale = true; };
  }, [runId]);

  if (done) return <span className="self-center text-[12.5px] text-neutral-500">{done}</span>;
  // Only the run on screen, and only while Wallet still has something to put
  // back. An older run is not offered at all: undoing one out of order is how you
  // revert a reading a later run has already replaced.
  if (!newest || newest.run_id !== runId || !undoable(newest)) return null;

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => { setConfirming(true); }}
        className="btn btn-danger btn-sm"
      >
        Undo run
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => {
          void rejectRun(runId)
            .then(() => { setDone("Run undone."); })
            .catch((exc: unknown) => {
              setDone(exc instanceof Error ? exc.message : "Could not undo the run.");
            });
        }}
        className="btn btn-destroy btn-sm"
      >
        Confirm undo
      </button>
      <button
        type="button"
        onClick={() => { setConfirming(false); }}
        className="btn btn-ghost btn-sm"
      >
        Keep
      </button>
    </>
  );
}
